import React, { useEffect, useState, useRef, useMemo } from "react";
import { fetchEndorsements } from "./api";

const EndorsementsSection = () => {
  const [endorsements, setEndorsements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(new Set());
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const [translatePx, setTranslatePx] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      // Check cache first
      const cached = sessionStorage.getItem('endorsements_cache');
      const cacheTime = sessionStorage.getItem('endorsements_cache_time');
      
      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        // Use cache if less than 5 minutes old
        if (age < 5 * 60 * 1000) {
          if (mounted) {
            setEndorsements(JSON.parse(cached));
            setLoading(false);
          }
          return;
        }
      }
      
      try {
        const data = await fetchEndorsements();
        if (mounted && Array.isArray(data)) {
          setEndorsements(data);
          // Cache the results
          sessionStorage.setItem('endorsements_cache', JSON.stringify(data));
          sessionStorage.setItem('endorsements_cache_time', Date.now().toString());
        }
      } catch (e) {
        // Try to use stale cache on error
        if (cached && mounted) {
          setEndorsements(JSON.parse(cached));
        }
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Responsive items per view
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      // Tailwind-like breakpoints: md >=768 => 3, lg >=1024 => 4
      if (w >= 1024) return 4;
      if (w >= 768) return 3;
      return 1;
    };

    const apply = () => {
      const newVal = calc();
      setItemsPerView((prev) => {
        if (prev !== newVal) return newVal;
        return prev;
      });
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  // Keep currentIndex valid when itemsPerView or endorsements change
  useEffect(() => {
    const maxStart = Math.max(0, endorsements.length - itemsPerView);
    setCurrentIndex((idx) => Math.min(idx, maxStart));
  }, [itemsPerView, endorsements.length]);

  // Recalculate pixel translate when container size, currentIndex or itemsPerView change
  useEffect(() => {
    const calc = () => {
      const container = containerRef.current;
      if (!container) return setTranslatePx(0);
      const itemWidth = container.clientWidth / itemsPerView;
      setTranslatePx(currentIndex * itemWidth);
    };

    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [currentIndex, itemsPerView, endorsements.length]);

  // Autoplay: advance one card every 2 seconds, wrap to start when reaching the end
  useEffect(() => {
    if (endorsements.length === 0) return;
    // If modal is open, pause autoplay
    if (selectedImage) return;

    const maxStart = Math.max(0, endorsements.length - itemsPerView);
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev < maxStart ? prev + 1 : 0));
    }, 2000);

    return () => clearInterval(id);
  }, [endorsements.length, itemsPerView, selectedImage]);

  const handleDotClick = (idx) => {
    const maxStart = Math.max(0, endorsements.length - itemsPerView);
    const newIndex = Math.min(Math.max(0, idx), maxStart);
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const maxStart = Math.max(0, endorsements.length - itemsPerView);
    setCurrentIndex((prev) => Math.min(prev + 1, maxStart));
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swiped left - go to next
        goToNext();
      } else {
        // Swiped right - go to previous
        goToPrev();
      }
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Handle image click to show full size
  const handleImageClick = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Handle click outside modal to close
  const handleModalClick = (e) => {
    if (e.target.classList.contains('modal-backdrop')) {
      handleCloseModal();
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      }
    };

    const navEl = document.querySelector('.fixed.top-0.left-0.right-0');
    const prevNavZ = navEl ? navEl.style.zIndex : null;

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
      // Lower navbar z-index so modal sits above it (some layouts change z unexpectedly)
      if (navEl) navEl.style.zIndex = '5';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
      // Restore navbar z-index to 50 when modal closes
      if (navEl) navEl.style.zIndex = '50';
      // If there was an inline zIndex before, restore it instead (best-effort)
      if (navEl && prevNavZ) navEl.style.zIndex = prevNavZ;
    };
  }, [selectedImage]);

  // Skeleton loader
  if (loading) {
    return (
      <section className="w-full px-3 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4 sm:mb-6">
            <div className="inline-block h-6 w-32 bg-gray-200 animate-pulse rounded"></div>
          </div>
          <div className="text-center mb-6 sm:mb-10 lg:mb-12">
            <div className="h-8 sm:h-10 lg:h-12 w-64 bg-gray-200 animate-pulse rounded mx-auto"></div>
          </div>
          <div className="flex justify-center gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`${i > 1 ? 'hidden md:block' : ''} ${i > 3 ? 'hidden lg:block' : ''}`}>
                <div className="w-48 h-48 bg-gray-200 animate-pulse rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (endorsements.length === 0) return null;

  return (
    <>
      <section className="w-full px-3 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto">
          {/* Trusted Support Badge - Responsive */}
          <div className="text-center mb-4 sm:mb-6">
            <span className="inline-block px-3 py-1 text-xs sm:text-sm font-semibold tracking-wider uppercase sup-heading">
              Trusted Support
            </span>
          </div>
          
          {/* Heading - Responsive Sizing */}
          <h2 className="text-center font-bold mb-6 sm:mb-10 lg:mb-12 
            text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl
            leading-tight sm:leading-tight md:leading-tight px-2">
            Our Endorsements
          </h2>

          {/* Slider Track - per-item carousel with dots navigation */}
          <div className="relative">
            {/* Previous Button */}
            {currentIndex > 0 && (
              <button
                onClick={goToPrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-1.5 sm:p-2 md:p-3 transition-all duration-200 hover:scale-110 -ml-2 sm:-ml-4 md:-ml-6"
                aria-label="Previous endorsement"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {currentIndex < Math.max(0, endorsements.length - itemsPerView) && (
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-1.5 sm:p-2 md:p-3 transition-all duration-200 hover:scale-110 -mr-2 sm:-mr-4 md:-mr-6"
                aria-label="Next endorsement"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div 
              ref={containerRef}
              className="overflow-hidden mx-auto"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                ref={trackRef}
                className="flex transition-transform duration-500 ease-out"
                style={{
                  transform: `translateX(-${translatePx}px)`,
                  width: `${(endorsements.length * 100) / itemsPerView}%`,
                }}
              >
              {endorsements.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col items-center group px-1 sm:px-2 md:px-3"
                  style={{ flex: `0 0 ${100 / endorsements.length}%`, maxWidth: `${100 / endorsements.length}%` }}
                >
                  <div className="w-full flex justify-center">
                    <div className="
                      w-[85vw] max-w-[280px] sm:max-w-[220px] md:max-w-[240px] lg:max-w-[260px]
                      aspect-square overflow-hidden mb-3 sm:mb-4 md:mb-6 cursor-pointer bg-gray-100"
                      onClick={() => handleImageClick(item.image, item.title)}>
                      <img
                        src={item.image}
                        alt={item.title || "Endorsement"}
                        className="w-full h-full object-contain transition-all duration-500 ease-out group-hover:scale-105 group-hover:opacity-90 group-active:scale-95"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                      />
                    </div>
                  </div>

                  {item.title && (
                    <div className="mt-1 sm:mt-2 md:mt-3 text-center w-full px-1 sm:px-2">
                      <p className="text-gray-700 font-medium text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl leading-snug sm:leading-relaxed break-words hyphens-auto">
                        {item.title}
                      </p>
                    </div>
                  )}
                </div>
              ))}  
            </div>
          </div>
        </div>

          {/* Dots navigation: one dot per endorsement; clicking shows that item as first visible */}
          <div className="mt-4 sm:mt-6 flex justify-center items-center">
            <div className="flex gap-1.5 sm:gap-2">
              {(() => {
                const maxStart = Math.max(0, endorsements.length - itemsPerView);
                const dots = Array.from({ length: maxStart + 1 }, (_, i) => i);
                return dots.map((dotIdx) => {
                  const isActive = dotIdx === currentIndex;
                  return (
                    <button
                      key={dotIdx}
                      onClick={() => handleDotClick(dotIdx)}
                      aria-label={`Show endorsements starting at ${dotIdx + 1}`}
                      className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${isActive ? 'bg-gray-800' : 'bg-gray-300 hover:bg-gray-400'}`}
                    />
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Full-size Image Modal - Just image with X button on it */}
      {selectedImage && (
        <div 
          className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={handleModalClick}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button - Positioned on the image */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 hover:scale-110"
              aria-label="Close full-size image"
            >
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image only - no container */}
            <img
              src={selectedImage.url}
              alt={selectedImage.title || "Full size endorsement"}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default EndorsementsSection;